import type { Pace, PlanAction, PlanPoint, PlanWarning, Schedule } from "@69ly/shared";
import type { PlanActor } from "./plan.ts";

/** One stop of a route: the callout it ends in, and how it was reached (run, walk, crouch). */
export interface RouteStop {
  name: string | null;
  paces: Pace[];
  to: PlanPoint;
}

/**
 * What a player does, as a reader sees it: moves in a row are one route, every
 * other action stands on its own. Times are seconds since the window started.
 */
export type Beat =
  | { kind: "route"; key: string; ids: string[]; start: number | null; end: number | null; stops: RouteStop[]; linked: boolean; warnings: PlanWarning[] }
  | { kind: "action"; key: string; action: PlanAction; start: number | null; end: number | null; lands: number | null; facing: string | null; warnings: PlanWarning[] };

/** How far ahead of a holding player we look for the callout they face, in world units. */
const FACING_REACH = 650;

/**
 * A player's beats. `placeAt` names the callout of a spot ("T Ramp"); stops in a
 * row with the same name merge, keeping each pace they were reached at.
 */
export function planBeats(actor: PlanActor, schedule: Schedule, placeAt: (p: PlanPoint) => string | null): Beat[] {
  const out: Beat[] = [];
  const warningsOf = (id: string) => schedule.warnings.filter((w) => w.action === id);
  const segments = schedule.actors.get(actor.id)?.segments ?? [];
  for (const a of actor.actions as PlanAction[]) {
    const time = schedule.times.get(a.id) ?? null;
    if (a.kind === "move") {
      const name = placeAt(a.to);
      const last = out[out.length - 1];
      if (last?.kind === "route") {
        last.ids.push(a.id);
        if (time) last.end = time.end;
        last.linked ||= !!a.after;
        last.warnings.push(...warningsOf(a.id));
        const stop = last.stops[last.stops.length - 1];
        if (stop && stop.name === name && name !== null) {
          stop.paces.push(a.pace);
          stop.to = a.to;
        } else last.stops.push({ name, paces: [a.pace], to: a.to });
        continue;
      }
      out.push({ kind: "route", key: a.id, ids: [a.id], start: time?.start ?? null, end: time?.end ?? null, stops: [{ name, paces: [a.pace], to: a.to }], linked: !!a.after, warnings: warningsOf(a.id) });
      continue;
    }
    let facing: string | null = null;
    if (a.kind === "hold") {
      const seg = segments.find((s) => s.action === a.id && s.kind === "hold");
      if (seg) {
        const rad = (a.yaw * Math.PI) / 180;
        const ahead = placeAt({ x: seg.from.x + Math.cos(rad) * FACING_REACH, y: seg.from.y + Math.sin(rad) * FACING_REACH, z: seg.from.z ?? null });
        facing = ahead && ahead !== placeAt(seg.from) ? ahead : null;
      }
    }
    out.push({ kind: "action", key: a.id, action: a, start: time?.start ?? null, end: time?.end ?? null, lands: time?.lands ?? null, facing, warnings: warningsOf(a.id) });
  }
  return out;
}

/** The ids of a beat's actions: a route's moves, or the one action. */
export const beatIds = (b: Beat) => (b.kind === "route" ? b.ids : [b.key]);

/** Done, happening, or still to come at this moment. */
export function beatState(b: Beat, now: number): "is-done" | "is-now" | "" {
  if (b.start === null || b.end === null) return "";
  if (now >= b.end && b.end > b.start) return "is-done";
  if (now >= b.start && now < Math.max(b.end, b.start + 0.6)) return "is-now";
  return "";
}
